import React, { useCallback } from 'react'
import Trans from 'next-translate/Trans'
import CarrierCurrencyComponent from 'tennders-common/components/CarrierCurrency.tsx'
import { GSelect } from 'tennders-common/form/components'
import { MYBOARD_UPDATE_STATUS } from 'tennders-stracture/libs/permissions'
import useStatusUpdate from 'tennders-transactions/hooks/useStatusUpdate'
import DistanceInKm from 'tennders-common/components/DistanceInKM'
import ShipperCurrency from 'tennders-common/components/ShipperCurrency'
import usePermissions from 'tennders-auth/hooks/usePermissions'
import useFetchOptions from 'tennders-common/hooks/useFetchOptions'
import GHref from 'tennders-market/common/components/element/GHref'
import dynamic from 'next/dynamic'
import useTransactionDetailStore from 'tennders-transactions/stores/useTransactionDetailStore'
import { useRouter } from 'next/router'
import { isNil } from 'lodash'
import DOMPurify from 'dompurify'
import useSession from 'tennders-auth/hooks/useSession'
import useTranslation from 'next-translate/useTranslation'
import { STATUS_UNAVAILABLE } from 'tennders-stracture/libs/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import IconDoubleArrow from '../../../../icons/icon-double-arrow.svg'
import IconCo2 from '../../../../icons/co2.svg'

const EllipsisToolTip = dynamic(() => import('ellipsis-tooltip-react-chan'), { ssr: false })

const ShipperPaymentTerm = ({ transaction }) => (
  isNil(transaction?.matchedLoad?.bookRate)
    ? null
    : (
      <div className="h5 mb-0 bold">
        <ShipperCurrency
          transaction={transaction}
          detailsClass="color-mid-gray h6 mb-0"
          withoutDetail
          label={() => <b>S{' '}</b>}
        />
      </div>
    ))

const CarrierPaymentTerm = ({ transaction }) => (
  isNil(transaction?.matchedTruck?.bookRate)
    ? null
    : (
      <div className="h5 mb-0 bold">
        <CarrierCurrencyComponent
          transaction={transaction}
          withoutDetail
          detailsClass="color-mid-gray h6 mb-0"
          carrierShipperLabel={() => <b>C{' '}</b>}
        />
      </div>
    ))

const DetailHeader = (props) => {
  const { children, close } = props
  const { t } = useTranslation()
  const { ableTo } = usePermissions()
  const { setstatusupdate } = useStatusUpdate()
  const { options, shipperCarrierStatusesCO } = useFetchOptions()
  const [transaction] = useTransactionDetailStore(state => [state.detail])
  const [assignTruckLoadType] = useTransactionDetailStore(state => [state.assignTruckLoadType])
  const { isBrokerUser, isShipperUser, isCarrierUser } = useSession()
  const { pathname } = useRouter()

  const getBGColor = useCallback(() => {
    let bgColor = ''
    const tValue = transaction?.status?.value
    if (tValue) {
      bgColor = ' bg-status-color-' + tValue
    }
    return bgColor
  }, [transaction?.status?.value])

  const getTitle = useCallback(() => {
    if (pathname?.includes('loadboard') || (pathname === '/' && isShipperUser)) {
      return t('transaction:loadDetails')
    }
    if (pathname?.includes('truckboard') || (pathname === '/' && isCarrierUser)) {
      return t('transaction:truck_details')
    }
    if (assignTruckLoadType === 'truck') {
      return t('transaction:assign_truck')
    }
    if (assignTruckLoadType === 'load') {
      return t('transaction:assign_load')
    }
    if (assignTruckLoadType === 'truck-match') {
      return t('transaction:matchTrucks')
    }
    if (assignTruckLoadType === 'load-match') {
      return t('transaction:matchLoads')
    }
    return t('transaction:transaction_details')
  }, [pathname, assignTruckLoadType, isShipperUser, isCarrierUser])

  return (
    <header className="detailed-header">
      <h2
        className="bold mb-0 border-r pr-12 mr-12 color-s"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getTitle()) }}
      />
      <div className="border-r pr-12 mr-12">
        <div className="h4 mb-0 bold">
          {transaction?.referenceNumber}
        </div>
        <div className="color-mid-gray h6 mb-0">{transaction?.createdAt?.dT}</div>
      </div>
      <div className="text-center">
        <div className="h4 mb-0 bold">{transaction?.pickupRoute?.label}</div>
        <div className="color-mid-gray h6 mb-0">{transaction?.pickupRoute?.date?.f}</div>
      </div>
      <div className="mx-24 color-mid-gray h6 mb-0 text-center lh-1">
        <div><DistanceInKm value={transaction?.distanceInKms} /></div>
        <IconDoubleArrow />
        <div><DistanceInKm value={transaction?.emptyKms} /></div>
      </div>
      <div className="border-r pr-12 mr-12 text-center">
        {transaction?.deliveryRoute ? (
          <>
            <div className="h4 mb-0 bold">{transaction?.deliveryRoute?.label}</div>
            <div className="color-mid-gray h6 mb-0">{transaction?.deliveryRoute?.date?.f}</div>
          </>
        ) : (
          <div className="color-mid-gray h6 mb-0 h6 mb-0">
            <Trans i18nKey="transaction:notDefined" />
          </div>
        )}
      </div>
      {transaction?.matchedLoad?.company?.name || transaction?.matchedTruck?.company?.name ? (
        <div className="border-r pr-12 mr-12 text-center load-truck-company-name">
          <div className="h4 mb-0 bold conform-medium-text">
            <EllipsisToolTip options={{ className: 'custom-color-tooltip' }}>
              {transaction?.matchedLoad?.company?.name || ''}
            </EllipsisToolTip>
          </div>
          <div className="bold conform-medium-text">
            <EllipsisToolTip options={{ className: 'custom-color-tooltip' }}>
              {transaction?.matchedTruck?.company?.name || ''}
            </EllipsisToolTip>
          </div>
        </div>
      ) : null}
      <div className="d-flex flex-col border-r pr-12 mr-12 text-center">
        { transaction?.status?.label === STATUS_UNAVAILABLE ? <span>N/A</span>
          : (
            <>
              <ShipperPaymentTerm transaction={transaction} />
              <CarrierPaymentTerm transaction={transaction} />
            </>
          )}
      </div>
      <div className="border-r pr-12 mr-12 text-center">
        <div className="text-center">
          {transaction?.isStatusChangeable && !transaction.statusLocked && (!transaction?.matchedTruck?.isMarketPlaced) ? (
            <GSelect
              onlyInput
              className={'form-select small max-width-100-px' + (getBGColor())}
              eClass="w-100"
              disabled={!ableTo([MYBOARD_UPDATE_STATUS])}
              onSet={(stat) => {
                if (stat > 14 && !isBrokerUser) {
                  return window.alert(t('transaction:status_locked'))
                }
                setstatusupdate({
                  action: stat,
                  transaction
                })
              }}
              translate={isBrokerUser}
              value={transaction?.status?.value}
              groups={isBrokerUser ? options?.groupedTransactionStatus : shipperCarrierStatusesCO}
            />
          ) : transaction?.status?.label}
        </div>
      </div>
      <div className="border-r pr-12 mr-12 text-center">
        <div className="h4 mb-0 d-flex justify-center align-center">
          {children}
        </div>
      </div>
      <div className="d-flex align-center text-12">
        <IconCo2 />
        <div className="d-flex flex-col ml-4">
          <span className="mb-2">{t('tableRow:co2_details_total', { total: transaction?.co2details?.full })}</span>
          <span className="mb-2">{t('tableRow:co2_details_empty', { empty: transaction?.co2details?.empty })}</span>
          <span className="mb-2">{t('tableRow:co2_details_loaded', { loaded: transaction?.co2details?.loaded })}</span>
        </div>
      </div>
      <GHref onClick={close} style={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end', marginLeft: 20, marginRight: 20 }}>
        <FontAwesomeIcon icon={faXmark} fontSize={25} />
      </GHref>
    </header>
  )
}

export default DetailHeader
